import React from 'react';
import { Modal, Descriptions, Button, Table } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { invoiceService } from '../services';
import { formatCurrency } from '../utils/format';
import { InvoiceItem } from '../types';
import dayjs from 'dayjs';
import './InvoiceDetailModal.css';

interface InvoiceDetailModalProps {
  visible: boolean;
  invoiceId: number | null;
  onClose: () => void;
}


const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  visible,
  invoiceId,
  onClose,
}) => {
  const { data: invoiceResponse, isLoading } = useQuery({
    queryKey: ['invoice-detail', invoiceId],
    queryFn: () => {
      if (!invoiceId) throw new Error('No invoice ID provided');
      return invoiceService.getDetail(invoiceId);
    },
    enabled: !!invoiceId && visible,
  });

  const invoice = invoiceResponse?.data;

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = dayjs(dateString);
    return date.format('DD/MM/YYYY HH:mm');
  };

  const handleDownload = () => {
    if (invoice) {
      console.log('Download invoice:', invoice.invoice_number);
    }
  };

  const itemColumns = [
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'center' as const,
    },
    {
      title: 'Đơn giá',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 150,
      align: 'right' as const,
      render: (price: string) => formatCurrency(parseFloat(price)),
    },
    {
      title: 'Thành tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      align: 'right' as const,
      render: (amount: string) => formatCurrency(parseFloat(amount)),
    },
  ];

  if (!invoice || isLoading) return null;

  return (
    <Modal
      title={`Chi tiết hóa đơn ${invoice.invoice_number}`}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Đóng
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
        >
          Tải về
        </Button>,
      ]}
      width={800}
      className="invoice-detail-modal"
    >
      <Descriptions bordered column={1} className="invoice-details">
        <Descriptions.Item label="Số hóa đơn">
          {invoice.invoice_number}
        </Descriptions.Item>
        <Descriptions.Item label="Tên khách hàng">
          {invoice.customer_name}
        </Descriptions.Item>
        <Descriptions.Item label="CCCD/MST">
          {invoice.customer_tax_code}
        </Descriptions.Item>
        <Descriptions.Item label="Dịch vụ">
          {invoice.service_name}
        </Descriptions.Item>
        <Descriptions.Item label="Tổng tiền">
          <span style={{ fontWeight: 600, fontSize: 16, color: '#385da4' }}>
            {formatCurrency(parseFloat(invoice.total_amount))}
          </span>
        </Descriptions.Item>
        <Descriptions.Item label="Ngày tạo đơn">
          {formatDateTime(invoice.created_at)}
        </Descriptions.Item>
        <Descriptions.Item label="Ngày gửi đơn">
          {formatDateTime(invoice.sent_at)}
        </Descriptions.Item>
      </Descriptions>

      {invoice.items && invoice.items.length > 0 && (
        <div className="invoice-items-section">
          <h4>Chi tiết dịch vụ</h4>
          <Table
            columns={itemColumns}
            dataSource={invoice.items.map((item: InvoiceItem, index: number) => ({
              ...item,
              key: index,
            }))}
            pagination={false}
            size="small"
          />
        </div>
      )}
    </Modal>
  );
};

export default InvoiceDetailModal;

